import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import booleanPointOnLine from "@turf/boolean-point-on-line";
import { Feature, LineString, point } from "@turf/helpers";
import { GeoJsonProperties, Geometry } from "geojson";
import { defer, isEqual } from "lodash";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { inSpan } from "fond/layers";
import { safeNearestPointOnLine } from "fond/turf";
import { pairwise } from "fond/utils/array";

const { constants: Constants, modes } = MapboxDraw;

/**
 * This function retrieves the ID of a feature.
 * It checks if the feature has an ID property or if it has a properties object with an ID property.
 * This is required as Mapbox Draw does not always set the ID property on the feature (e.g., when a feature is created).
 *
 * @param feature - The feature to be checked
 * @returns The ID of the feature, or undefined if no ID is found.
 */
export const getFeatureId = (feature: DrawFeature | Feature<Geometry, GeoJsonProperties>): string | number | undefined =>
  feature.id || feature.properties?.id;

/**
 * Splits a path into two paths at the given pole location.
 * This is used when a pole is dropped on a path, and we want to split the path into two segments.
 * The new path is created with the coordinates of the original path up to the nearest point on the line,
 * and the coordinates of the new pole.
 * The original path is deleted and replaced with the two new paths.
 *
 * @param this - The current context of the DrawCustomModeThis
 * @param path - The path to be split
 * @param pole - The pole location where the path will be split
 * @returns An array of new paths created from the split
 */
export function splitPaths(this: DrawCustomModeThis, path: DrawLineString, pole: DrawPoint): MapboxDraw.DrawLineString[] {
  const newFeatures: MapboxDraw.DrawLineString[] = [];
  const nearestPoint = safeNearestPointOnLine(path, pole);

  if (nearestPoint && nearestPoint.properties && nearestPoint.properties.dist !== 0) {
    const newCoords = [...path.getCoordinates()];
    newCoords.splice(nearestPoint.properties.index + 1, 0, pole.getCoordinates());

    // Determine the new features based on splitting the path at the pole location
    pairwise(newCoords).forEach((coordinates) => {
      const newF = this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: { layerId: inSpan },
        geometry: {
          type: Constants.geojsonTypes.LINE_STRING,
          coordinates,
        },
      }) as DrawLineString;

      newFeatures.push(newF);
      this.addFeature(newF);
    });

    // We defer for the split features creation & the deletion of the
    // original feature.
    defer(() => {
      const featureId = getFeatureId(path);
      if (featureId) {
        this.deleteFeature(featureId, { silent: true });
      }
      newFeatures.forEach((newF) => {
        this.addFeature(newF);
      });
    });
  }

  return newFeatures;
}

/**
 * This function checks for collocated features (spans or poles) at the given feature's location.
 * It uses the Mapbox GL JS library to project the feature's coordinates to screen coordinates,
 * and then queries the map for features within a bounding box around the projected coordinates.
 *
 * @param this - The current context of the DrawCustomModeThis
 * @param feature - The feature to be checked for collocated features
 * @param connectedIds - The set of connected IDs to be excluded from the search
 * @param type - The type of event (e.g. "mouseup") to be used for filtering
 * @returns An object containing two arrays: spans and poles.
 *          The spans array contains the collocated spans (linestrings) found at the feature's location,
 *          and the poles array contains the collocated poles (points) found at the feature's location.
 */
export function pullInFeatures(
  this: DrawCustomModeThis,
  feature: DrawFeature,
  connectedIds?: Set<string | number>,
  type?: MapboxDraw.MapMouseEvent["type"]
): { spans: DrawLineString[]; poles: DrawPoint[] } {
  const { x, y } = this._ctx.map.project(feature.getCoordinates() as mapboxgl.LngLatLike);
  const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [x - 15, y - 15],
    [x + 15, y + 15],
  ];
  const allFeatures = this.featuresAt(null, queryBbox, "click") as Feature[];
  const collocatedSpans: DrawLineString[] = [];
  const collocatedPoles: DrawPoint[] = [];

  if (feature.type === "Point") {
    allFeatures
      // On mouseup we exclude the already connected spans
      .filter((existingFeature) => !(type === "mouseup" && connectedIds?.has(existingFeature.properties?.id)))
      .forEach((existingFeature) => {
        const existingFeatureId = getFeatureId(existingFeature);

        if (existingFeatureId) {
          // Get the related DrawFeature as we need to compare coordinates
          // found only on DrawFeatures, not Feature (which have different precisions)
          const drawFeature: DrawFeature | undefined = this.getFeature(existingFeatureId);

          if (drawFeature?.type === "LineString") {
            // Check if the point is being dropped onto an existing vertex
            if (drawFeature.getCoordinates().some((c) => booleanEqual(point(c), point(feature.getCoordinates()), { precision: 5 }))) {
              collocatedSpans.push(drawFeature);
            } else if (booleanPointOnLine(feature.getCoordinates(), existingFeature as Feature<LineString>, { epsilon: 0.0000001 })) {
              // Check if the point is being dropped onto a line
              collocatedSpans.push(drawFeature);
            }
          } else if (drawFeature?.type === "Point" && existingFeatureId !== getFeatureId(feature)) {
            // Check if a pole is being dropped onto an existing pole
            if (booleanEqual(drawFeature, feature, { precision: 6 })) {
              collocatedPoles.push(drawFeature);
            }
          }
        }
      });
  }

  return {
    spans: collocatedSpans,
    poles: collocatedPoles,
  };
}

/**
 * This function retrieves the poles at either end of a span.
 * It uses the pullInFeatures function to check for collocated features at the span's coordinates.
 *
 * @param this - The current context of the DrawCustomModeThis
 * @param feature - The feature to be checked for poles
 * @returns An array of DrawPoint objects representing the poles at either end of the span.
 */
export function getSpanPoles(this: DrawCustomModeThis, feature: DrawLineString): DrawPoint[] {
  const poles: DrawPoint[] = [];
  // If deleting a span we need to validate that the poles connected are
  // now unsnapped
  feature.getCoordinates().forEach((coordinates) => {
    const { poles: p } = pullInFeatures.call(
      this,
      this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: {},
        geometry: {
          type: Constants.geojsonTypes.POINT,
          coordinates,
        },
      })
    );

    poles.push(...p);
  });

  return poles;
}

/**
 * This function checks if a feature is snapped to any other features.
 * It sets the DRAWING_CONSTANTS.Unsnapped property of the feature to "true" if it is not snapped to any other features,
 * allowing for styles to be applied based on this property.
 *
 * @param this - The current context of the DrawCustomModeThis
 * @param feature - The feature to be checked for snapped status
 */
export function refreshSnappedStatus(this: DrawCustomModeThis, feature: DrawPoint): void {
  const { spans } = pullInFeatures.call(this, feature);
  feature.setProperty(DRAWING_CONSTANTS.Unsnapped, Boolean(spans.length === 0).toString());
}

/**
 * This function checks if a span is valid by ensuring that not all coordinates are the same.
 *
 * @param feature - The feature to be checked for valid span
 * @returns A boolean indicating whether the span is valid (true) or not (false).
 */
export const validSpan = (feature: DrawLineString): boolean => {
  const coordinates = feature.getCoordinates();
  return !coordinates.every((coords) => isEqual(coordinates[0], coords));
};
